import Vue from "vue";

import VueAxios from "vue-axios";
import VueAuthenticate from "vue-authenticate";
import { LoginReason, isCredentialPath } from "./session";

Vue.use(VueAxios, axios);

export default VueAuthenticate.factory(Vue.prototype.$http, {
  baseUrl: env.API_URL, // Your API domain
  loginUrl: env.API_URL + "/login"
});

/**
 * Whether the bootstrap failed because the credentials are dead, as opposed to
 * the server being unreachable. Only the former is worth telling the user about
 * on the login screen (RR-330); optional chaining because a network error
 * carries no response at all, and reading .status off undefined used to throw
 * inside this very catch block and swallow the rejection.
 */
const catchAuthFail = (response, store) => {
  const revoked = response?.status == 401 || response?.status == 400;

  if (revoked) {
    store.dispatch("logoutOffline");
  }

  return revoked;
};

/**
 * Rejects with a `notices.login_reasons` key when the session is provably gone,
 * and with null when the bootstrap merely failed. main.js turns the former into
 * /login?reason=...
 */
export const handleAuth = store => {
  // Nothing on a credential page needs a session, and the visitor is there
  // precisely because theirs no longer works. Renewing it can only reload the tab
  // or bounce them to /login - both of which lose the single-use token that the
  // reset link put in the URL.
  if (isCredentialPath()) return Promise.resolve();

  return new Promise((resolve, reject) => {
    // catchAuthFail reads .status off a response, so it must be handed e.response
    // and not the error: handed the error itself it never saw a 401, and a revoked
    // session was left with isAuthenticated set and no user behind it - the state
    // the router guard cannot resolve.
    const fail = e =>
      reject(catchAuthFail(e?.response, store) ? LoginReason.SESSION_REVOKED : null);

    if (store.getters.shouldRefreshToken) {
      // No location.reload() once this succeeds. It was there to get the rest of
      // the app to pick up the renewed session, which it does on its own:
      // vue-authenticate reads the token out of storage per request, and fetchUser
      // commits into the store every component already renders from. What the
      // reload did do was refresh the tab, seconds after load, on every visitor
      // whose access token was within a fortnight of expiring - which is most
      // people who have not signed in for a while, and so most people following a
      // password reset link.
      return store
        .dispatch("refreshAccessToken")
        .then(() => store.dispatch("fetchUser"))
        .then(() => resolve())
        .catch(fail);
    }

    if (store.getters.isAuthenticated) {
      return store
        .dispatch("fetchUser")
        .then(() => resolve())
        .catch(fail);
    }

    return resolve(
      store.dispatch("setAppearance", store.getters.appearance.toLowerCase())
    );
  });
};
